import type { SessionProgressionMode } from '@zeus/index'
import type { PathChapter } from '~/pages/backoffice/admin/sessions/path-chapters'
import type { PathStep } from '~/pages/backoffice/admin/sessions/path-step'
import { ScoreRetention } from '@zeus/index'
import { sectionIndexAt } from '~/pages/backoffice/admin/sessions/path-chapters'

/** Score minimum proposé quand le module prérequis ne déclare pas de seuil. */
const FALLBACK_MIN_SCORE = 0.7

type ProgressionMode = `${SessionProgressionMode}`

/** Un module de parcours tel que l'API le renvoie, ce qu'il faut pour l'éditer. */
export type StoredPathModule = {
  unlockAt?: unknown
  prerequisiteModuleId?: string | null
  prerequisiteMinScore?: number | null
  allowRetake?: boolean | null
  scoreRetention?: ScoreRetention | null
  /** Chapitre du module (#75). */
  sectionId?: string | null
  module?: { id: string } | null
}

/**
 * Un module ajouté au parcours : ouvert dès le début, sans prérequis, qu'on peut
 * refaire en gardant le meilleur score — le comportement d'avant #80 et #84.
 */
export function newPathStep(moduleId: string): PathStep {
  return {
    moduleId,
    unlockAt: '',
    prerequisiteModuleId: null,
    prerequisiteMinScore: null,
    allowRetake: true,
    scoreRetention: ScoreRetention.BEST,
  }
}

/**
 * Un jour saisi (`AAAA-MM-JJ`) tel que l'API l'attend : elle le résout dans le
 * fuseau de l'organisation (#194). Vide devient `null` et non `undefined` : en
 * mise à jour, `null` retire la borne, `undefined` la laisserait telle quelle.
 */
export function toDayOrNull(value: string): string | null {
  return value || null
}

/** L'état de l'éditeur, depuis le parcours enregistré. */
export function pathStepsFrom(modules: StoredPathModule[], toDay: (value: unknown) => string): PathStep[] {
  return modules.flatMap(sm => (sm.module?.id
    ? [{
        ...newPathStep(sm.module.id),
        unlockAt: toDay(sm.unlockAt),
        prerequisiteModuleId: sm.prerequisiteModuleId ?? null,
        prerequisiteMinScore: sm.prerequisiteMinScore ?? null,
        allowRetake: sm.allowRetake ?? true,
        scoreRetention: sm.scoreRetention ?? ScoreRetention.BEST,
      }]
    : []))
}

/** L'input de l'API, depuis l'état de l'éditeur ; chaque module porte l'indice de son chapitre (#75). */
export function toPathInput(steps: PathStep[], chapters: PathChapter[] = []) {
  return steps.map((s, position) => ({
    moduleId: s.moduleId,
    unlockAt: toDayOrNull(s.unlockAt),
    prerequisiteModuleId: s.prerequisiteModuleId,
    prerequisiteMinScore: s.prerequisiteModuleId ? s.prerequisiteMinScore : null,
    allowRetake: s.allowRetake,
    scoreRetention: s.scoreRetention,
    sectionIndex: sectionIndexAt(chapters, position),
  }))
}

/** Les chapitres à envoyer avec le parcours, dans l'ordre (#75). */
export function toSectionsInput(chapters: PathChapter[]) {
  return chapters.map(chapter => ({ title: chapter.title.trim() }))
}

/**
 * Retire un module du parcours, et le prérequis des modules qui le
 * désignaient : l'API refuserait un prérequis hors du parcours, l'admin ne doit
 * pas l'apprendre à l'enregistrement.
 */
export function withoutStep(steps: PathStep[], moduleId: string): PathStep[] {
  return steps
    .filter(s => s.moduleId !== moduleId)
    .map(s => (s.prerequisiteModuleId === moduleId
      ? { ...s, prerequisiteModuleId: null, prerequisiteMinScore: null }
      : s))
}

/** Pour chaque module, ceux qu'il attend : son prérequis, et en séquentiel le précédent. */
function waitsFor(steps: PathStep[], mode: ProgressionMode): Map<string, string[]> {
  return new Map(steps.map((s, index) => {
    const previous = steps[index - 1]
    return [s.moduleId, [
      ...(mode === 'SEQUENTIAL' && previous ? [previous.moduleId] : []),
      ...(s.prerequisiteModuleId ? [s.prerequisiteModuleId] : []),
    ]]
  }))
}

/** Vrai si `from` attend `target`, directement ou par une chaîne de modules. */
function reaches(graph: Map<string, string[]>, from: string, target: string): boolean {
  const seen = new Set<string>()
  const stack = [from]
  while (stack.length > 0) {
    const id = stack.pop() as string
    if (id === target) return true
    if (seen.has(id)) continue
    seen.add(id)
    stack.push(...(graph.get(id) ?? []))
  }
  return false
}

/**
 * Prérequis proposables pour un module : tous les autres, le précédent en
 * premier — c'est le cas le plus courant, sans être imposé (#80).
 *
 * Un module qui attend déjà celui-ci — par son prérequis, une chaîne, ou
 * l'ordre séquentiel — n'est pas proposé : les deux s'attendraient, et aucun
 * ne s'ouvrirait. L'API refuse cette boucle (`module-prerequisites.ts`) ;
 * l'éditeur évite de la laisser composer. Le prérequis DÉJÀ choisi reste
 * proposé même s'il est devenu une boucle (passage en séquentiel,
 * réordonnancement) : le retirer de la liste le ferait disparaître sans bruit —
 * `createsLoop` le signale.
 */
export function prerequisiteOptions(
  steps: PathStep[],
  moduleId: string,
  mode: ProgressionMode,
): { moduleId: string, previous: boolean }[] {
  const index = steps.findIndex(s => s.moduleId === moduleId)
  const current = steps[index]?.prerequisiteModuleId
  const previous = index > 0 ? steps[index - 1]?.moduleId : undefined
  const graph = waitsFor(steps, mode)
  const allowed = (candidate: string) => candidate === current || !reaches(graph, candidate, moduleId)

  const others = steps.filter(s => s.moduleId !== moduleId && s.moduleId !== previous && allowed(s.moduleId))
  return [
    ...(previous && allowed(previous) ? [{ moduleId: previous, previous: true }] : []),
    ...others.map(s => ({ moduleId: s.moduleId, previous: false })),
  ]
}

/** Vrai si le prérequis choisi pour ce module forme une boucle : il ne s'ouvrirait jamais. */
export function createsLoop(steps: PathStep[], moduleId: string, mode: ProgressionMode): boolean {
  const prerequisite = steps.find(s => s.moduleId === moduleId)?.prerequisiteModuleId
  return prerequisite != null && reaches(waitsFor(steps, mode), prerequisite, moduleId)
}

/**
 * Un score sur [0..1] ramené au pourcent entier : ce que l'éditeur affiche est
 * ce qui s'enregistre, et ce que l'apprenant lira (« Obtenez 76 % »).
 */
function wholePercent(score: number): number {
  return Math.round(score * 100) / 100
}

/** Score minimum proposé : le seuil de réussite du module prérequis, s'il en déclare un. */
export function defaultMinScore(masteryScore: number | null | undefined): number {
  return wholePercent(masteryScore ?? FALLBACK_MIN_SCORE)
}

/** Score minimum depuis la saisie en pourcentage, borné à [0, 100]. */
export function minScoreFromPercent(input: string): number {
  const pct = Math.min(100, Math.max(0, Number(input) || 0))
  return wholePercent(pct / 100)
}

/**
 * Nouveau prérequis choisi. Si un score était exigé, il repart du seuil du
 * NOUVEAU module : garder l'ancien ferait hériter l'admin, sans qu'il le voie,
 * du seuil d'un autre module. Sans prérequis, plus de score.
 */
export function withPrerequisite(
  currentMinScore: number | null,
  next: { moduleId: string, masteryScore?: number | null } | undefined,
): Pick<PathStep, 'prerequisiteModuleId' | 'prerequisiteMinScore'> {
  if (!next) return { prerequisiteModuleId: null, prerequisiteMinScore: null }
  return {
    prerequisiteModuleId: next.moduleId,
    prerequisiteMinScore: currentMinScore == null ? null : defaultMinScore(next.masteryScore),
  }
}
